'use client';

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Box } from '@terra/ui-styled-system';
import type { ColDef, RowClickedEvent } from 'ag-grid-community';
import { Table } from '../../components/table';
import { DrawerCollapsible } from './DrawerCollapsible';
import type { DrawerCollapsibleVariant } from './DrawerCollapsible';

export type DrawerTableProps<T> = {
  title: string;
  columnDefs: ColDef<T>[];
  rowData: T[];
  onViewAllClick?: () => void;
  onRowClick?: (row: T) => void;
  maxHeight?: string;
  defaultOpen?: boolean;
  action?: ReactNode;
  variant?: DrawerCollapsibleVariant;
};

export function DrawerTable<T extends { id: string }>({
  title,
  columnDefs,
  rowData,
  onViewAllClick,
  onRowClick,
  maxHeight = '500px',
  defaultOpen = true,
  action,
  variant,
}: DrawerTableProps<T>) {
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressMovable: true,
    }),
    [],
  );

  if (rowData.length === 0) {
    return null;
  }

  return (
    <DrawerCollapsible
      title={title}
      onViewAllClick={onViewAllClick}
      defaultOpen={defaultOpen}
      action={action}
      variant={variant}
    >
      <Box w="full" maxH={maxHeight} overflow="auto">
        <Table<T>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout="autoHeight"
          suppressCellFocus
          hideColumnControls
          onRowClicked={
            onRowClick
              ? (event: RowClickedEvent<T>) => {
                  if (event.data) {
                    onRowClick(event.data);
                  }
                }
              : undefined
          }
        />
      </Box>
    </DrawerCollapsible>
  );
}
